/**
 * AI Lead Scoring Dashboard
 * Lead qualification and prioritization dashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Target,
  TrendingUp,
  Flame,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AILeadScoringDashboard = ({ user, onNavigate }) => {
  const [leads, setLeads] = useState([]);
  const [topLeads, setTopLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, topLeadsRes, metricsRes] = await Promise.all([
        fetch(`${API}/phase9/leads?limit=100`, { headers: authHeaders() }),
        // The "Top 20 Highest Opportunity Leads" panel needs the true
        // top-scoring leads across the whole org, not just the 20
        // highest-scored among the 100 most-recently-created ones -
        // sort server-side instead of approximating from the recency
        // page above.
        fetch(`${API}/phase9/leads?sort_by=ai_lead_score&sort_order=desc&limit=20`, { headers: authHeaders() }),
        fetch(`${API}/phase9/leads/metrics`, { headers: authHeaders() })
      ]);

      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData.leads || []);
      }

      if (topLeadsRes.ok) {
        const topLeadsData = await topLeadsRes.json();
        setTopLeads((topLeadsData.leads || []).filter(l => l.ai_lead_score !== null && l.ai_lead_score !== undefined));
      }

      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }
    } catch (error) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBulkScore = async () => {
    setScoring(true);
    try {
      const response = await fetch(`${API}/phase9/leads/bulk-score`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: {} })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Scored ${data.scored_count} leads successfully`);
        await fetchData();
      } else {
        toast.error('Bulk scoring failed');
      }
    } catch (error) {
      toast.error('Error during bulk scoring');
    } finally {
      setScoring(false);
    }
  };

  const getScoreBadgeClass = (label) => {
    switch (label) {
      case 'Hot': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'Warm': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'Cold': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'Bad Fit': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  const scoredLeads = leads.filter(l => l.ai_lead_score !== null && l.ai_lead_score !== undefined);

  const MetricCard = ({ label, value, icon: Icon, color = 'text-white', loading }) => (
    <article className="glass-panel">
      {loading ? (
        <>
          <Skeleton className="h-4 w-20 mb-2 bg-white/10" />
          <Skeleton className="h-8 w-32 bg-white/10" />
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Icon className={`h-5 w-5 ${color}`} />
            <p className="text-[11px] uppercase tracking-[0.08em] text-white/60">{label}</p>
          </div>
          <strong className={`text-3xl font-semibold ${color}`}>{value}</strong>
        </>
      )}
    </article>
  );

  return (
    <div className="page-stack" data-testid="ai-lead-scoring-dashboard">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9C — AI Lead Scoring</p>
          <h2>Lead Scoring Dashboard</h2>
          <div className="hero-stats">
            <span>{scoredLeads.length} Scored</span>
            <span>{leads.length - scoredLeads.length} Unscored</span>
            <span>{metrics?.hot_leads || 0} Hot Leads</span>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            className="primary-btn"
            onClick={handleBulkScore}
            disabled={scoring || loading}
            data-testid="bulk-score-all-btn"
          >
            <Target size={16} />
            {scoring ? 'Scoring...' : 'Score All Unscored Leads'}
          </Button>
          <Button className="ghost-btn" onClick={fetchData}>
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>
      </section>

      {/* Score Distribution */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Hot Leads (80-100)"
          value={metrics?.hot_leads || 0}
          icon={Flame}
          color="text-red-400"
          loading={loading}
        />
        <MetricCard
          label="Warm Leads (60-79)"
          value={metrics?.warm_leads || 0}
          icon={TrendingUp}
          color="text-orange-400"
          loading={loading}
        />
        <MetricCard
          label="Cold Leads (40-59)"
          value={metrics?.cold_leads || 0}
          icon={Target}
          color="text-blue-400"
          loading={loading}
        />
        <MetricCard
          label="Bad Fit (0-39)"
          value={metrics?.bad_fit_leads || 0}
          icon={Target}
          color="text-gray-400"
          loading={loading}
        />
      </section>

      {/* Top Scored Leads */}
      <section className="glass-panel">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white/90">Top 20 Highest Opportunity Leads</h3>
            <p className="text-sm text-white/60 mt-1">Prioritized by AI lead score</p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full bg-white/10" />
            ))}
          </div>
        ) : topLeads.length === 0 ? (
          <div className="text-center py-16">
            <Target className="h-16 w-16 text-white/20 mx-auto mb-4" />
            <h4 className="text-base font-semibold text-white/90 mb-2">No scored leads yet</h4>
            <p className="text-white/60 mb-6">Score your leads to see them prioritized here</p>
            <Button className="primary-btn" onClick={handleBulkScore} disabled={scoring}>
              <Target size={16} />
              Score All Leads
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {topLeads.map((lead, idx) => (
              <div
                key={lead.id}
                className="flex items-center justify-between p-4 border border-white/10 rounded-xl hover:bg-white/[0.02] hover:border-[var(--line-gold)] transition-all cursor-pointer"
                onClick={() => onNavigate('detail', lead)}
                data-testid={`top-lead-${idx}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-[var(--gold)]/10 flex items-center justify-center border border-[var(--gold)]/30">
                    <span className="text-sm font-bold text-[var(--gold)]">{idx + 1}</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white/90">{lead.clinic_name}</h4>
                    <p className="text-xs text-white/60">{lead.city}, {lead.state}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {lead.rating && (
                    <div className="text-xs text-white/70">
                      ⭐ {lead.rating} ({lead.review_count ?? lead.reviews_count ?? 0})
                    </div>
                  )}
                  <Badge className={`${getScoreBadgeClass(lead.ai_lead_score_label)} text-sm px-3 py-1.5 font-mono`}>
                    {lead.ai_lead_score} — {lead.ai_lead_score_label}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Scoring Methodology */}
      <section className="glass-panel">
        <h3 className="text-lg font-semibold text-white/90 mb-4">AI Scoring Methodology</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-semibold text-white/80 mb-2">Scoring Factors (0-100 points)</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <span className="text-[var(--gold)] mt-1">•</span>
                <div>
                  <strong className="text-white/90">Website Quality (20 pts):</strong> Online presence and booking capability
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[var(--gold)] mt-1">•</span>
                <div>
                  <strong className="text-white/90">Online Presence (20 pts):</strong> Google Business, ratings, reviews, social media
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[var(--gold)] mt-1">•</span>
                <div>
                  <strong className="text-white/90">Market Signals (20 pts):</strong> High-value location, category fit
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[var(--gold)] mt-1">•</span>
                <div>
                  <strong className="text-white/90">Opportunity Signals (20 pts):</strong> Growth potential indicators
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[var(--gold)] mt-1">•</span>
                <div>
                  <strong className="text-white/90">Ability to Pay (20 pts):</strong> Business success indicators
                </div>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white/80 mb-2">Score Labels</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-red-500/20 text-red-300 border-red-500/30">Hot (80-100)</Badge>
                <span className="text-xs text-white/70">Immediate outreach priority</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30">Warm (60-79)</Badge>
                <span className="text-xs text-white/70">Strong potential, follow-up sequence</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">Cold (40-59)</Badge>
                <span className="text-xs text-white/70">Nurture with content</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Bad Fit (0-39)</Badge>
                <span className="text-xs text-white/70">Low priority or disqualify</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AILeadScoringDashboard;
